// T066: Create post composer dialog
// T067: Create compose button in header
// T069: Add character count indicator
// T079: Validate minimum content length
// T080: Handle insufficient RC with warning message
// T081: Handle transaction errors with retry option
// T082: Show success notification with post link

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MarkdownEditor } from './MarkdownEditor'
import { ImageUpload } from './ImageUpload'
import { useCreatePost, useCheckRC } from '@/hooks/usePost'
import { extractImagesFromMarkdown } from '@/services/imageUpload'
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react'
import { toast } from 'sonner'
import { getPostUrl } from '@/utils/permlink'
import { useAuthStore } from '@/stores/authStore'

interface PostComposerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const MIN_CONTENT_LENGTH = 100

export function PostComposer({ open, onOpenChange }: PostComposerProps) {
  const { user } = useAuthStore()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [tags, setTags] = useState('')
  const [uploadedImages, setUploadedImages] = useState<string[]>([])

  const createPost = useCreatePost()
  const { hasEnoughRC, rcPercentage, minRequired } = useCheckRC()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (body.length < MIN_CONTENT_LENGTH) {
      toast.error(`Post must be at least ${MIN_CONTENT_LENGTH} characters long`)
      return
    }

    if (!hasEnoughRC) {
      toast.error(
        `Insufficient Resource Credits. You need at least ${minRequired}% RC to post. Current: ${rcPercentage.toFixed(1)}%`,
        {
          duration: 5000,
        }
      )
      return
    }

    try {
      // Extract images from markdown and combine with uploaded images
      const markdownImages = extractImagesFromMarkdown(body)
      const allImages = [...new Set([...uploadedImages, ...markdownImages])]

      // Parse tags
      const tagList = tags
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean)

      // Create post
      const result = await createPost.mutateAsync({
        title: title.trim() || undefined,
        body,
        tags: tagList.length > 0 ? tagList : undefined,
        images: allImages.length > 0 ? allImages : undefined,
      })

      // Success!
      if (result.permlink && user) {
        const primaryTag = tagList[0] || 'fast-steemit'
        const url = getPostUrl(primaryTag, user.username, result.permlink)

        toast.success('Post published successfully!', {
          description: (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              View on Steemit
            </a>
          ),
          duration: 5000,
        })

        // Reset form and close
        setTitle('')
        setBody('')
        setTags('')
        setUploadedImages([])
        onOpenChange(false)
      }
    } catch (error) {
      // Error handling with retry option
      const errorMessage = error instanceof Error ? error.message : 'Failed to publish post'
      toast.error('Failed to publish post', {
        description: errorMessage,
        action: {
          label: 'Retry',
          onClick: () => handleSubmit(e),
        },
        duration: 7000,
      })
    }
  }

  const handleImageUploaded = (url: string) => {
    setUploadedImages((prev) => [...prev, url])
    // Optionally insert markdown image syntax into the body
    const imageMarkdown = `\n![Image](${url})\n`
    setBody((prev) => prev + imageMarkdown)
  }

  const handleImageRemove = (url: string) => {
    setUploadedImages((prev) => prev.filter((img) => img !== url))
  }

  const isFormValid = body.length >= MIN_CONTENT_LENGTH && hasEnoughRC
  const isSubmitting = createPost.isPending

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          fullscreenOnMobile
          className="flex flex-col overflow-hidden sm:max-h-[90vh] sm:max-w-3xl"
        >
          <DialogHeader className="shrink-0">
            <DialogTitle>Create New Post</DialogTitle>
            <DialogDescription>
              Write and publish a new post to Steemit blockchain.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col space-y-4 overflow-y-auto sm:space-y-6">
            {/* RC Warning */}
            {!hasEnoughRC && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive bg-destructive/10 p-4">
                <AlertCircle className="h-5 w-5 text-destructive" />
                <div className="flex-1 text-sm">
                  <p className="font-medium text-destructive">Insufficient Resource Credits</p>
                  <p className="mt-1 text-muted-foreground">
                    You need at least {minRequired}% RC to post. Current: {rcPercentage.toFixed(1)}%.
                    Please wait for your RC to recharge.
                  </p>
                </div>
              </div>
            )}

            {/* Title (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="title">
                Title <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter a title (auto-generated if empty)"
                maxLength={255}
                disabled={isSubmitting}
              />
              <p className="text-sm text-muted-foreground">
                Leave empty to auto-generate from your content
              </p>
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label htmlFor="content">
                Content <span className="text-destructive">*</span>
              </Label>
              <MarkdownEditor
                value={body}
                onChange={setBody}
                minLength={MIN_CONTENT_LENGTH}
                disabled={isSubmitting}
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label htmlFor="tags">
                Tags <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="tag1, tag2, tag3"
                disabled={isSubmitting}
              />
              <p className="text-sm text-muted-foreground">
                Comma-separated tags. If empty, &quot;fast-steemit&quot; will be used as default.
              </p>
            </div>

            {/* Images */}
            <div className="space-y-2">
              <Label>Images</Label>
              <ImageUpload
                onImageUploaded={handleImageUploaded}
                onRemove={handleImageRemove}
                uploadedImages={uploadedImages}
              />
            </div>

            {/* Spacer to push buttons to bottom on mobile */}
            <div className="flex-1 sm:hidden" />

            {/* Form Actions */}
            <div className="sticky bottom-0 -mx-4 shrink-0 border-t bg-background px-4 py-4 sm:static sm:mx-0 sm:px-0">
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isFormValid || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Publish Post
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
